import React, { useRef, useState } from 'react';

import {
    SectionHeading,
    TableCustom,
    WrapperAdvancedSearch
} from '../../components/common';
import DataTaggerModalApi from '../../components/controls/DataTaggerModalApi';
import { useEntidad, useLista, useManagedContext, useFilterPersistence, useNavigateWithFilters } from '../../components/hooks';
import { createSearchWithSavedFilters } from '../../utils/searchWithSavedFilters';
import { usePermissions } from '../../components/hooks/usePermissions';
import { APIS } from '../../config/apis';
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { PERMISSIONS } from '../../consts/permissions';
import { ServerRequest } from '../../utils/apiweb';
import { getDateSerialize, getDateToString } from '../../utils/convert';
import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import {TIPS} from "../../consts/tips";


function PlanPagoDefinicionesView() {
    const { showMessageModal, setIsLoading } = useManagedContext()

    const [state, setState] = useState({
        loading: false,
        list: []
    })
    const [dataTaggerState, setDataTaggerState] = useState({})
    const [tableVisible, setTableVisible] = useState(false)

    const searchRef = useRef()
    const tableRef = useRef()

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.PLAN_PAGO_DEFINICION_EDIT);

    const callbackNoSuccess = (response) => {
        response.json()
            .then((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
                setState(prevState => ({...prevState, loading: false}));
            })
            .catch((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
                setState(prevState => ({...prevState, loading: false}));
            });
    }

    const callbackError = (error) => {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando solicitud: ' + error.message)
        setState(prevState => ({...prevState, loading: false}));
    }

    const SearchPlanesWithSavedFilters = createSearchWithSavedFilters({
        setState,
        setTableVisible,
        apiUrl: APIS.URLS.PLAN_PAGO_DEFINICION,
        buildParamsUrl: (filters) => {
            return `/filter?` +
                `idTipoPlanPago=${filters.idTipoPlanPago || 0}&` +
                `idTipoTributo=${filters.idTipoTributo || 0}&` +
                `idTasaPlanPago=${filters.idTasaPlanPago || 0}&` +
                `idSubTasaPlanPago=${filters.idSubTasaPlanPago || 0}&` +
                `idEstadoPlanPagoDefinicion=${filters.idEstadoPlanPagoDefinicion || 0}&` +
                `fechaDesde=${encodeURIComponent(getDateSerialize(filters.fechaDesde))}&` +
                `fechaHasta=${encodeURIComponent(getDateSerialize(filters.fechaHasta))}&` +
                `descripcion=${filters.descripcion || ''}&` +
                `etiqueta=${filters.etiqueta || ''}&` +
                `codigo=${encodeURIComponent(filters.codigo || '')}`;
        },
        callbackNoSuccess,
        callbackError,
        onSuccess: () => {
            setState(prevState => {
                const list = prevState.list.map(x => {
                    if (x.fechaCreacion) x.fechaCreacion = new Date(x.fechaCreacion);
                    if (x.fechaDesde) x.fechaDesde = new Date(x.fechaDesde);
                    if (x.fechaHasta) x.fechaHasta = new Date(x.fechaHasta);
                    if (x.fechaDesdeAlcanceTemporal) x.fechaDesdeAlcanceTemporal = new Date(x.fechaDesdeAlcanceTemporal);
                    if (x.fechaHastaAlcanceTemporal) x.fechaHastaAlcanceTemporal = new Date(x.fechaHastaAlcanceTemporal);
                    return x;
                });
                return {...prevState, list};
            });
        }
    });

    const { getCurrentFilters, restoredPageIndex, restoredGlobalFilter } = useFilterPersistence(
        searchRef,
        tableRef,
        SearchPlanesWithSavedFilters
    );
    const navigateWithFilters = useNavigateWithFilters(getCurrentFilters);

    //#region entidades/listas
    const [getEntidades, _getEntidad, entidadesReady] = useEntidad({
        entidades: ['TipoPlanPago', 'Tasa', 'SubTasa'],
        onLoaded: (entidades, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
        },
        memo: {
            key: 'PlanPagoDefinicionesEntidades',
            timeout: 0
          },
    })
    const getEntidad = (type, id) => _getEntidad(type, id) ?? {}

    const [, _getRowLista ] = useLista({
        listas: ['TipoTributo', 'EstadoPlanPagoDefinicion'],
        onLoaded: (listas, isSuccess, error) => {
          if (!isSuccess) {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        },
        memo: {
          key: 'PlanPagoDefinicionesListas',
          timeout: 0
        },
    })
    const getRowLista = (lista, id) => _getRowLista(lista, id) ?? {}
    //#endregion

    //#region events
    const avmr = {
        onAdd: () => {
            navigateWithFilters(`/plan-pago-definicion/${OPERATION_MODE.NEW}`);
        },
        onView: ({ id }) => {
            navigateWithFilters(`/plan-pago-definicion/${OPERATION_MODE.VIEW}/${id}`);
        },
        onModify: ({ id }) => {
            navigateWithFilters(`/plan-pago-definicion/${OPERATION_MODE.EDIT}/${id}`);
        },
        onClone: ({ id }) => {
            const plan = state.list.find(x => x.id === id);
            showMessageModal({
                ...MESSAGE_MODALS.CLONAR,
                message: `¿Está seguro de clonar la definición de plan de pago ${plan.codigo}?`,
                onConfirm: () => confirmCloneId(id),
            })
        },
        onRemove: ({ id }) => {
            const plan = state.list.find(x => x.id === id);
            showMessageModal({
                ...MESSAGE_MODALS.BORRAR,
                message: `¿Está seguro de borrar la definición de plan de pago ${plan.codigo}?`,
                onConfirm: () => confirmRemoveId(id),
            })
        },
        onAdditionalInfo: ({ id }, disabled) => {
            setDataTaggerState({ id, disabled })
        }
    }

    const confirmRemoveId = (id) => {
        setState(prevState => ({...prevState, loading: true}));
        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.PLAN_PAGO_DEFINICION,
            `/${id}`,
            null,
            () => { 
                setState(prevState => ({
                    ...prevState,
                    loading: false,
                    list: prevState.list.filter((x) => x.id !== id)
                }));
            },
            callbackNoSuccess,
            callbackError,
        )
    }

    const confirmCloneId = (id) => {
        setState(prevState => ({...prevState, loading: true}));
        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.PLAN_PAGO_DEFINICION,
            `/clone/${id}`,
            null,
            () => {
                setState(prevState => ({...prevState, loading: false}));
                ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'Definición de plan de pago clonada');
                onSearch();
            },
            callbackNoSuccess,
            callbackError,
        )
    }

    const onSearch = () => {
        SearchPlanesWithSavedFilters(getCurrentFilters());
    }
    //#endregion

    const subTasaCell = ({ value }) => {
        const item = getEntidad('SubTasa', value)
        if (item.codigo && item.descripcion) return `${item.codigo} - ${item.descripcion}`
        else return ''
    }

    const dataTaggerRegistro = state.list.find(x => x.id === dataTaggerState.id)?.descripcion ?? ''

    return (
        <>

        <SectionHeading titles={[{ title: 'Financiación' }, { title: 'Definición de planes de pago' }]}
                        onAddClick={canEdit ? avmr.onAdd : null} tableVisible={tableVisible} />

        <section className='section-accordion'>
            <WrapperAdvancedSearch
                ref={searchRef}
                onSearch={onSearch}
                filters={[
                    { title: 'Convenio', tipName: TIPS.NAME.MODALS.TIPO_PLAN_PAGO_MODAL, field: 'idTipoPlanPago', type: 'entity', entidad: 'TipoPlanPago', size: 4 },
                    { title: 'Tipo de tributo', field: 'idTipoTributo', type: 'list', lista: 'TipoTributo', size: 2 },
                    { title: 'Estado', field: 'idEstadoPlanPagoDefinicion', type: 'selectLista', lista: 'EstadoPlanPagoDefinicion', size: 2 },
                    { title: 'Etiqueta', field: 'etiqueta', type: 'string', size: 4 },
                    { title: 'Tasa del plan de pago', field: 'idTasaPlanPago', type: 'tasa', size: 4 },
                    { title: 'Sub tasa del plan de pago', field: 'idSubTasaPlanPago', type: 'subtasa', fieldTasa: 'idTasaPlanPago', size: 4 },
                    { title: 'Fecha desde', field: 'fechaDesde', type: 'date', size: 2 },
                    { title: 'Fecha hasta', field: 'fechaHasta', minValueField: 'fechaDesde', type: 'date', size: 2 },
                    { title: 'Descripción', field: 'descripcion', type: 'string', size: 4 },
                    { title: 'Código', field: 'codigo', type: 'string', size: 4 },
                ]}
            />

            {tableVisible && (
                <TableCustom
                    ref={tableRef}
                    className={'TableCustomBase m-top-20'}
                    data={state.list}
                    messageEmpty="No se encontraron resultados"
                    columns={[
                        { Header: 'Plan pago', accessor: 'idTipoPlanPago', Cell: ({ value }) => getEntidad('TipoPlanPago', value).codigo ?? '', width: '10%', },
                        { Header: 'Tipo de tributo', accessor: 'idTipoTributo', Cell: ({ value }) => getRowLista('TipoTributo', value).nombre ?? '', width: '10%', },
                        { Header: 'Tasa', accessor: 'idTasaPlanPago', Cell: ({ value }) => getEntidad('Tasa', value).codigo ?? '', width: '10%', },
                        { Header: 'Sub tasa', accessor: 'idSubTasaPlanPago', Cell: subTasaCell, width: '10%', },
                        { Header: 'Código', accessor: 'codigo', width: '10%', },
                        { Header: 'Descripción', accessor: 'descripcion', width: '10%', },
                        { Header: 'Desde', accessor: 'fechaDesde', Cell: ({ value }) => getDateToString(value, false), width: '7%', },
                        { Header: 'Hasta', accessor: 'fechaHasta', Cell: ({ value }) => getDateToString(value, false), width: '7%', },
                        { Header: 'Estado', accessor: 'idEstadoPlanPagoDefinicion', Cell: ({ value }) => getRowLista('EstadoPlanPagoDefinicion', value).nombre ?? '',  width: '7%', },
                    ]}
                    avmr={avmr}
                    editCode={PERMISSIONS.PLAN_PAGO_DEFINICION_EDIT}
                    restoredPageIndex={restoredPageIndex}
                    defaultGlobalFilter={restoredGlobalFilter}
                />
            )}

            {dataTaggerState.id &&
                <DataTaggerModalApi
                    title="Información adicional de plan de pago"
                    registro={dataTaggerRegistro}
                    tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_PLAN_PAGO_MODAL}
                    entidad="PlanPagoDefinicion"
                    idEntidad={dataTaggerState.id}
                    disabled={dataTaggerState.disabled}
                    onConfirm={() => setDataTaggerState({})}
                    onDismiss={() => setDataTaggerState({})}
                />
            }

        </section>

        </>
    )
}

export default PlanPagoDefinicionesView;
